import React, { ReactElement } from 'react';
import { Menu } from 'antd';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ENV_API_TYPE } from 'common/constants/env.constants';
import { IS_ADMIN_FE } from 'common/constants/general-access-role.constants';
import { EApiType } from 'common/enums/api-type.enum';
import { ROUTES_MAPPING } from 'navigation/constants/route.constants';
import { ReactComponent as AccountMngMenuIcon } from 'assets/menu-icons/account-mng-menu-icon.svg';
import { ReactComponent as AppealAndArbitrationMenuIcon } from 'assets/menu-icons/appeal-and-arbitration-menu-icon.svg';
import { ReactComponent as AssetsMenuIcon } from 'assets/menu-icons/assets-menu-icon.svg';
import { ReactComponent as CommissionAndConversionMenuIcon }
  from 'assets/menu-icons/commission-and-conversion-menu-icon.svg';
import { ReactComponent as DashboardMenuIcon } from 'assets/menu-icons/dashboard-menu-icon.svg';
import { ReactComponent as LimitsMenuIcon } from 'assets/menu-icons/limits-menu-icon.svg';
import { ReactComponent as PaymentOperationsMenuItem } from 'assets/menu-icons/payment-operations-menu-icon.svg';
import { ReactComponent as ReconMenuItem } from 'assets/menu-icons/recon-menu-icon.svg';
import { ReactComponent as ReportingMenuIcon } from 'assets/menu-icons/reporting-menu-icon.svg';
import { ReactComponent as UserRoleMngMenuIcon } from 'assets/menu-icons/user-role-mng-menu-icon.svg';
import { SContainer } from './left-side-menu.style';
import { dashboardSidebarHook } from '../../hooks/dashboard-sidebar.hook';

type TMenuItem = Required<MenuProps>['items'][number];

const SideBarMenu = (): ReactElement => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sidebar = dashboardSidebarHook();

  const menuItem = document.location.pathname.split('/')[1];
  const subMenuItem = document.location.pathname.split('/')[2];
  const activeMenuPath = [`/${menuItem}`, `/${menuItem}/${subMenuItem}`];

  const menuItems: TMenuItem[] = [
    {
      key: ROUTES_MAPPING.PROTECTED.DASHBOARD,
      icon: <DashboardMenuIcon />,
      label: t('mainMenu.issueAndBurnAssets'),
    },
    {
      key: ROUTES_MAPPING.PROTECTED.PAYMENT,
      icon: <PaymentOperationsMenuItem />,
      label: t('mainMenu.paymentOperation'),
    },
    {
      key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT,
      icon: <UserRoleMngMenuIcon />,
      label: t('mainMenu.userAndRoleManagement'),
      ...(!sidebar.collapsed && {
        children: [
          ...(ENV_API_TYPE === EApiType.Admin
            ? [
              {
                key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_COUNTERPARTIES,
                label: t('usrRoleMng.tabs.counterparties.title'),
              },
            ]
            : []),
          {
            key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCHES,
            label: t('usrRoleMng.tabs.branches.title'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_EMPLOYEES,
            label: t('usrRoleMng.tabs.employees.title'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_ROLES,
            label: t('usrRoleMng.tabs.roles.title'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_BRANCH_ROLES,
            label: t('usrRoleMng.tabs.branchRoles.title'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_CUSTOMERS,
            label: t('usrRoleMng.tabs.customers.title'),
          },
          ...(ENV_API_TYPE === EApiType.Admin
            ? [
                {
                  key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_MERCHANTS,
                  label: t('usrRoleMng.tabs.merchants.title'),
                },
                {
                  key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_PERMISSIONS,
                  label: t('usrRoleMng.tabs.permissions.title'),
                },
              ]
            : []),
          // {
          //   key: ROUTES_MAPPING.PROTECTED.USER_ROLE_MANAGEMENT_KYC,
          //   label: t('usrRoleMng.tabs.kycKyb'),
          // },
        ],
      }),
    },
    {
      key: ROUTES_MAPPING.PROTECTED.RECONCILIATION,
      icon: <ReconMenuItem />,
      label: t('mainMenu.reconciliation'),
      ...(!sidebar.collapsed && {
        children: [
          {
            key: ROUTES_MAPPING.PROTECTED.RECONCILIATION_STATISTIC,
            label: t('reconciliation.tabs.statistic.title'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.RECONCILIATION_TABLE,
            label: t('reconciliation.tabs.table.title'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.RECONCILIATION_NOTIFICATIONS,
            label: t('reconciliation.tabs.notifications.title'),
          },
        ],
      }),
    },
    {
      key: ROUTES_MAPPING.PROTECTED.REPORTING,
      icon: <ReportingMenuIcon />,
      label: t('mainMenu.reporting'),
      ...(!sidebar.collapsed && {
        children: [
          {
            key: ROUTES_MAPPING.PROTECTED.REPORTING_PAYMENT_OPERATIONS,
            label: t('reporting.tabs.paymentOperations'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.REPORTING_EMISSION_HISTORY,
            label: t('reporting.tabs.emissionHistory'),
          },
          ...(ENV_API_TYPE === EApiType.Admin
            ? [
                {
                  key: ROUTES_MAPPING.PROTECTED.REPORTING_AUDIT,
                  label: t('reporting.tabs.audit'),
                },
              ]
            : []
          ),
        ],
      }),
    },
    {
      key: ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT,
      icon: <AccountMngMenuIcon />,
      label: t('mainMenu.accountManagement'),
      ...(!sidebar.collapsed && {
        children: [
          {
            key: ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_EMISSION,
            label: t('accountManagement.tabs.counterpartyEmission.title'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_COUNTERPARTY_PROCESSING,
            label: t('accountManagement.tabs.counterpartyProcessing.title'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.ACCOUNT_MANAGEMENT_CUSTOMERS,
            label: t('accountManagement.tabs.customers.title'),
          },
        ],
      }),
    },
    {
      key: ROUTES_MAPPING.PROTECTED.LIMITS,
      icon: <LimitsMenuIcon />,
      label: t('mainMenu.limits'),
      ...(!sidebar.collapsed && {
        children: [
          {
            key: ROUTES_MAPPING.PROTECTED.LIMITS_STANDARD_LIMITS,
            label: t('limits.tabs.standardLimits.title'),
          },
          {
            key: ROUTES_MAPPING.PROTECTED.LIMITS_INDIVIDUAL_LIMITS,
            label: t('limits.tabs.individualLimits.title'),
          },
        ],
      }),
    },
    {
      key: ROUTES_MAPPING.PROTECTED.APPEAL_AND_ARBITRATION,
      icon: <AppealAndArbitrationMenuIcon />,
      label: t('mainMenu.appealAndArbitration'),
    },
    ...(IS_ADMIN_FE
      ? [
        {
          key: ROUTES_MAPPING.PROTECTED.ASSETS,
          icon: <AssetsMenuIcon />,
          label: t('mainMenu.assets'),
        },
        {
          key: ROUTES_MAPPING.PROTECTED.COMMISSION_AND_CONVERSION,
          icon: <CommissionAndConversionMenuIcon />,
          label: t('mainMenu.commissionAndConversion'),
        },
      ]
      : []),
  ];

  const menuClickHandler = ({ key }: { key: string }): void => {
    navigate(key);
  };

  return (
    <SContainer>
      <Menu
        mode="inline"
        multiple
        defaultOpenKeys={sidebar.collapsed ? [''] : activeMenuPath}
        selectedKeys={activeMenuPath}
        items={menuItems}
        onClick={menuClickHandler}
      />
    </SContainer>
  );
};

export default SideBarMenu;
